import { useMemo } from 'react';
import { useMutation } from 'urql';
import { MetaAdAccount } from '@frontend/gql/social/graphql';
import { useMessagingContext } from '@frontend/hooks';
import { logger } from '@common';
import { createMetaAdCampaign, createMetaAdSet, createMetaAdFromExistingPost } from '../../../../graphql';
import { createBoostPostServices } from '../factories/serviceFactory';
import { BoostPostServices } from '../types/services';

/**
 * Custom hook that provides boost post services to components
 *
 * This hook creates and memoizes the boost post services, ensuring they have
 * access to the latest GraphQL mutations and state management functions.
 */
export const useBoostPostServices = (
  setOptimisticMetaAdAccounts: React.Dispatch<React.SetStateAction<MetaAdAccount[] | undefined>>,
  getCurrentAdAccounts: () => MetaAdAccount[] | undefined,
): BoostPostServices => {
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();

  // GraphQL mutations
  const [, createCampaignMutation] = useMutation(createMetaAdCampaign);
  const [, createAdSetMutation] = useMutation(createMetaAdSet);
  const [, createAdMutation] = useMutation(createMetaAdFromExistingPost);

  // Memoize services to prevent recreation on every render
  const services = useMemo(() => {
    return createBoostPostServices({
      createCampaignMutation,
      createAdSetMutation,
      createAdMutation,
      setOptimisticMetaAdAccounts,
      getCurrentAdAccounts,
      showSuccessMessage,
      showErrorMessage,
      logger,
    });
  }, [
    createCampaignMutation,
    createAdSetMutation,
    createAdMutation,
    setOptimisticMetaAdAccounts,
    getCurrentAdAccounts,
    showSuccessMessage,
    showErrorMessage,
  ]);

  return services;
};
